// Path: ./src/app/pages/RolePage/index.tsx
import React, { useEffect, useState } from "react";
import { AccountCard } from "../../components/AccountCard";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { GridPaginationModel } from "@mui/x-data-grid";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import {
  GfRoleDto,
  GfPermissionDto,
  GfAdminUserDto,
  GfRole,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";
import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";

import { GridSortModel } from "@mui/x-data-grid";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { initialGridPaginationModel } from "../../../utils/constant/initialGridPaginationModel";
import { initialGridSortModel } from "../../../utils/constant/initialGridSortModel";
import { TableData } from "../../../utils/interface/tableData";
import { useRoleTableColumns } from "./useRoleTableColumns";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { routes } from "../../../utils/constant/navigationRoutes";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

interface roleInfoProps {
  roleName: string;
  listItems: (any | string)[];
}

const RolePage = () => {
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const [isTableLoading, setIsTableLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const { t } = useTranslation();
  const [rolesData, setRolesData] = useState<TableData<GfRole>>({
    columns: [],
    rows: [],
    total: 0,
  });
  const [roleInfo, setRoleInfo] = useState<roleInfoProps>({
    roleName: "",
    listItems: [],
  });
  // const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deleteRoleData, setDeleteRoleData] = useState<GfRoleDto | undefined>();
  const [userAttachedToRole, setUserAttachedToRole] = useState<
    GfAdminUserDto[] | []
  >([]);
  const [pagination, setPagination] = useState<GridPaginationModel>(
    initialGridPaginationModel,
  );
  const [sortModel, setSortModel] =
    React.useState<GridSortModel>(initialGridSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const [isLoadingUser, setIsLoadingUser] = useState(false);

  /**
   * Handles a click event and navigates to the role creation page.
   */
  const handleClick = () => {
    navigate(routes.role.create);
  };

  const AccountCardData = {
    cardTitle: t(messages.Role()),
    buttonText: t(messages.Add_Role()),
    permissionList: {
      title: t(messages.Role()),
      subTitle: roleInfo.roleName,
      bottomTitle: t(messages.Permission()),
      listItems: roleInfo.listItems,
    },
    /**
     * Handles a click event and navigates to the role creation page.
     */
    onClick: handleClick,
  };

  /**
   * Opens a confirmation modal to delete a role.
   *
   * @param value - The role data to be deleted.
   */
  const handleOpenDelete = async (value: GfRoleDto) => {
    setOpen(true);
    setDeleteRoleData(value);
    setIsLoadingUser(true);
  };
  const columns = useRoleTableColumns(handleOpenDelete);
  const fetchUserAttachedToThisRole = async () => {
    if (deleteRoleData) {
      const result =
        await adminApiService.gfAdminUserAdminControllerFindUsersByRole(
          deleteRoleData.id,
          authHeaders,
        );
      const response = await result.json();
      if (response.length) {
        setUserAttachedToRole(response);
      }
    }
    setIsLoadingUser(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setIsLoadingUser(false);
    setDeleteRoleData(undefined);
    setUserAttachedToRole([]);
  };

  const handleOnDelete = async () => {
    setIsTableLoading(true);

    try {
      if (deleteRoleData) {
        await adminApiService.deleteOneBaseGfRoleAdminControllerGfRole(
          deleteRoleData?.id,
          authHeaders,
        );
      }
      toastSuccess(
        `${deleteRoleData?.name} ${t(messages.Role_Deleted_SuccessFully())}`,
      );
    } catch (error) {
      console.log(error);
      toastError(t(messages.Error_occurred_while_deleting()));
    }
    setIsTableLoading(false);
    handleCancel();
    if (muiFilterModelHasActiveItems(filterModel)) {
      fetchRolesWithFilter(filterModel);
    } else {
      fetchRoles(pagination, sortModel);
    }
  };

  const fetchRolesWithFilter = async (activeFilterModel: GridFilterModel) => {
    await guardedListFetch(
      listRequestGuard,
      setIsTableLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfRoleAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setRolesData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
      (error) => {
        console.log(error);
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchRoles = async (
    paginationModel: GridPaginationModel,
    gridSortModel: GridSortModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setIsTableLoading,
      async () =>
        adminApiService.getManyBaseGfRoleAdminControllerGfRole(
          undefined,
          undefined,
          undefined,
          undefined,
          gridSortModel?.length
            ? [`${gridSortModel[0].field},${gridSortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
        ),
      (result) => {
        setRolesData((prev) => ({
          ...prev,
          rows: result?.data,
          total: result?.total,
        }));
      },
    );
  };

  const handleRowSelect = (value?: GridRowSelectionModel) => {
    console.log("handleRowSelect", value);
  };

  interface handleOnRowClick {
    columns: [];
    id: string;
    row: GfRoleDto;
  }

  interface ObjectTypes {
    key: string[];
    value: boolean;
  }

  interface accProps {
    coupon?: ObjectTypes[];
    deals?: ObjectTypes[];
    flyer?: ObjectTypes[];
    tag?: ObjectTypes[];
    upload?: ObjectTypes[];
    user?: ObjectTypes[];
  }
  const handleOnRowClick = (value?: handleOnRowClick) => {
    if (value?.row) {
      const groups = value?.row?.permissions?.reduce(
        (acc: accProps, obj: GfPermissionDto) => {
          const group = obj.name.split("-")[0] as keyof accProps;
          if (!acc[group]) {
            acc[group] = [];
          }
          acc[group]?.push({
            key: [obj.name.split("-")[1]],
            value: false,
          });
          return acc;
        },
        {},
      );

      const groupArray = Object.entries(groups).map(([key, value]) => ({
        group: key,
        objects: value as ObjectTypes[],
      }));

      const ItemList = groupArray?.map((value) => {
        return {
          title: value.group,
          itemsList: value?.objects?.map((val) => val?.key[0]),
        };
      });

      setRoleInfo({
        roleName: value?.row?.name,
        listItems: ItemList,
      });
    } else {
      setRoleInfo({
        roleName: "",
        listItems: [],
      });
    }
  };

  useEffect(() => {
    setRolesData({
      columns: columns,
    });
  }, []);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchRolesWithFilter(filterModel);
    } else {
      fetchRoles(pagination, sortModel);
    }
  }, [pagination, sortModel, filterModel]);

  useEffect(() => {
    if (open) {
      fetchUserAttachedToThisRole();
    }
  }, [open]);

  return (
    <SetupPageContainer data-testid="role-view-page">
      <AccountCard
        loadFailed={listRequestGuard.loadFailed}
        permissionGroupName="role"
        permissionRoleName="create"
        isLoading={isTableLoading}
        cardTitle={AccountCardData.cardTitle}
        buttonText={AccountCardData.buttonText}
        onClick={AccountCardData.onClick}
        permissionList={AccountCardData.permissionList}
        rolesData={rolesData}
        handleRowSelect={handleRowSelect}
        onRowClick={handleOnRowClick}
        initialPaginationModel={pagination}
        setPagination={setPagination}
        // sortModel={sortModel}
        setSortModel={setSortModel}
        buttonDataTestId={"add-role-button"}
        onTableChange={(
          gridPaginationModel: GridPaginationModel,
          gridSortModel: GridSortModel,
        ) => {
          setPagination(gridPaginationModel);
          setSortModel(gridSortModel);
        }}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
      />
      <MuiConfirmationModal
        open={open}
        handleCancel={handleCancel}
        handleConfirm={handleOnDelete}
        title={`${t(messages.Delete_Confirmation())}`}
        description={
          isLoadingUser ? (
            <Box sx={{ width: 300, display: "flex", justifyContent: "center" }}>
              <CircularProgress sx={{ color: "#ff813a" }} />
            </Box>
          ) : (
            <>
              {userAttachedToRole?.length ? (
                <Box sx={{ width: "100%" }}>
                  <Typography>
                    {t(messages.Are_you_sure_you_want_to_delete_This())}
                    {t(messages.Role())}
                    {"  "}
                    {deleteRoleData?.name}?{" "}
                    {t(messages.Attached_User_Will_Lose_This_Role())}
                    {t(messages.This_action_cannot_be_undone())}
                  </Typography>
                  <Typography variant="h6" mt={1}>
                    {t(messages.Users_Attached_to_this_role())}
                  </Typography>
                  <Stack
                    spacing={2}
                    sx={{
                      maxHeight: "350px",
                      overflowY: "scroll",
                      paddingBottom: 2,
                      paddingLeft: 1,
                      paddingRight: 4,
                    }}
                  >
                    {userAttachedToRole?.map((value) => {
                      return (
                        <Paper
                          key={value.userName}
                          sx={{ padding: 1 }}
                          data-testid="rolepage-test-id"
                        >
                          {t(messages.UserName())}: {value.userName},{" "}
                          {t(messages.Email())}: {value.email}
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <Typography>
                    {t(messages.Are_you_sure_you_want_to_delete_This())}
                    {t(messages.Role())}
                    {"  "}
                    {deleteRoleData?.name}?{" "}
                    {t(messages.Attached_User_Will_Lose_This_Role())}
                    {t(messages.This_action_cannot_be_undone())}
                  </Typography>
                  <Typography variant="h6" mt={1}>
                    {t(messages.Users_Attached_to_this_role())}
                  </Typography>
                  <Typography mt={1} pl={1}>
                    {t(messages.No_User_is_Attached())}
                  </Typography>
                </Box>
              )}
            </>
          )
        }
      />
      {/* <Backdrop sx={{ color: "#ff813a", zIndex: 999999 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop> */}
    </SetupPageContainer>
  );
};

export default RolePage;
